import {
    ConsentResult,
    PulseStorage,
    StorageChangeKeyMatch,
    StorageChangeOrigin,
} from '@pulse/common';
import { BehaviorSubject, Observable, Subject, debounceTime, map } from 'rxjs';

export class OneTrustConsentService {
    private cookieName = 'OptanonConsent';
    private boxCookieName = 'OptanonAlertBoxClosed';

    constructor(private unprefixedStorage: PulseStorage) {}

    changes(): Observable<ConsentResult> {
        const initialValue = this.parseOneTrustCookie();
        const stream = new BehaviorSubject<ConsentResult>(initialValue);
        const cookieStream = new Subject<void>();

        cookieStream
            .pipe(
                debounceTime(30),
                map(() => this.parseOneTrustCookie()),
            )
            .subscribe((r) => stream.next(r));

        this.unprefixedStorage
            .change(this.cookieName, StorageChangeKeyMatch.Full, StorageChangeOrigin.All)
            .subscribe(() => cookieStream.next());
        this.unprefixedStorage
            .change(this.boxCookieName, StorageChangeKeyMatch.Full, StorageChangeOrigin.All)
            .subscribe(() => cookieStream.next());

        return stream;
    }

    private parseOneTrustCookie(): ConsentResult {
        const cookieStr = this.unprefixedStorage.get(this.cookieName) || '';
        const params = new URLSearchParams(cookieStr || '');
        const groups = this.parseConsentGroups(params);

        if (!groups) {
            return {
                state: 'pending',
            };
        }

        const provider_consent_id = params.get('consentId') || undefined;
        const response_time = this.getResponseTime();
        const pending =
            !Number.isSafeInteger(response_time) || params.get('AwaitingReconsent') === 'true';

        const necessary_cookies =
            typeof groups['C0001'] === 'boolean' ? groups['C0001'] : undefined;
        const performance_cookies =
            typeof groups['C0002'] === 'boolean' ? groups['C0002'] : undefined;
        const functional_cookies =
            typeof groups['C0003'] === 'boolean' ? groups['C0003'] : undefined;
        const targeting_cookies =
            typeof groups['C0004'] === 'boolean' ? groups['C0004'] : undefined;
        const social_media_cookies =
            typeof groups['C0005'] === 'boolean' ? groups['C0005'] : undefined;

        return {
            state: pending ? 'pending' : 'responded',
            response_time,
            provider_consent_id,

            necessary_cookies,
            functional_cookies,
            performance_cookies,
            social_media_cookies,
            targeting_cookies,
        };
    }

    private parseConsentGroups(params: URLSearchParams): Record<string, boolean> | undefined {
        const groups = (params.get('groups') || '').trim();

        if (!groups) {
            return undefined;
        }

        return groups.split(',').reduce<Record<string, boolean>>((g, item) => {
            const parts = (item || '').split(':').map((i) => i.trim());
            const name = parts[0];
            const enabled = parts[1] === '1' || parts[1] === 'true';

            if (name && parts.length === 2) {
                g[name] = enabled;
            }

            return g;
        }, {});
    }

    private getResponseTime() {
        const responseDateValue = this.unprefixedStorage.get(this.boxCookieName);
        const responseDate = responseDateValue ? new Date(responseDateValue) : undefined;

        return responseDate?.getTime() || undefined;
    }
}
